import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import ChevronDown from '@/icons/ChevronDown';
import type { MediaLibraryFilters } from '@/types/media-library';

interface MediaFilterSource {
  id: string;
  title: string;
}

interface MediaLibraryFiltersBarProps {
  filters: MediaLibraryFilters;
  onChange: (filters: MediaLibraryFilters) => void;
  years: number[];
  sources: MediaFilterSource[];
}

const MediaLibraryFiltersBar: React.FC<MediaLibraryFiltersBarProps> = ({ filters, onChange, years, sources }) => {
  const { t } = useTranslation('screens');

  const setField = <K extends keyof MediaLibraryFilters>(key: K, value: MediaLibraryFilters[K]) => {
    onChange({ ...filters, [key]: value });
  };

  return (
    <div className="media-page__filter-selects" role="group" aria-label={t('filters.yearAndSource')}>
      <Select.Root value={filters.year} onValueChange={(value) => setField('year', value)}>
        <Select.Trigger className="media-page__filter-select" aria-label={t('filters.filterByYear')}>
          <Select.Value placeholder={t('filters.allYears')} />
          <Select.Icon className="media-page__filter-select-icon" aria-hidden>
            <ChevronDown width={14} height={14} />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content className="media-page__filter-select-menu" position="popper" sideOffset={6}>
            <Select.Viewport>
              <Select.Item value="all" className="media-page__filter-select-option">
                <Select.ItemText>{t('filters.allYears')}</Select.ItemText>
              </Select.Item>
              {years.map((year) => (
                <Select.Item key={year} value={String(year)} className="media-page__filter-select-option">
                  <Select.ItemText>{year}</Select.ItemText>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>

      <Select.Root value={filters.sourceId} onValueChange={(value) => setField('sourceId', value)}>
        <Select.Trigger className="media-page__filter-select" aria-label={t('filters.filterBySource')}>
          <Select.Value placeholder={t('filters.allSources')} />
          <Select.Icon className="media-page__filter-select-icon" aria-hidden>
            <ChevronDown width={14} height={14} />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content className="media-page__filter-select-menu" position="popper" sideOffset={6}>
            <Select.Viewport>
              <Select.Item value="all" className="media-page__filter-select-option">
                <Select.ItemText>{t('filters.allSources')}</Select.ItemText>
              </Select.Item>
              {sources.map((source) => (
                <Select.Item key={source.id} value={source.id} className="media-page__filter-select-option">
                  <Select.ItemText>{source.title}</Select.ItemText>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  );
};

export default MediaLibraryFiltersBar;
